import React, { useState, useEffect } from "react";
import {
  Typography,
  Space,
  Card,
  Form,
  Button,
  Select,
  InputNumber,
  Table,
  Row,
  Col,
  Input,
  Badge,
  Divider,
  Tooltip,
  Modal,
  Collapse,
  Radio,
  Slider,
  Alert,
} from "antd";
import {
  CalculatorOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  BlockOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { useCompany } from "../hooks/useCompany";
import {
  useCompanyProducts,
  CompanyProduct,
} from "../hooks/useCompanyProducts";
import { trackFeatureUsage } from "../lib/posthog";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Constants for EPAL pallet dimensions (in cm)
const PALLET_WIDTH = 80;
const PALLET_LENGTH = 120;
const PALLET_HEIGHT = 14.4; // Standard EPAL pallet height in cm
const MAX_HEIGHT = 180; // Maximum height in cm (1.8m including pallet)
const AVAILABLE_HEIGHT = MAX_HEIGHT - PALLET_HEIGHT;

// Predefined colors for products (RGBA format for transparency)
const PRODUCT_COLORS = [
  "rgba(24, 144, 255, 0.6)", // Blue
  "rgba(82, 196, 26, 0.6)", // Green
  "rgba(245, 34, 45, 0.6)", // Red
  "rgba(250, 173, 20, 0.6)", // Orange
  "rgba(114, 46, 209, 0.6)", // Purple
  "rgba(19, 194, 194, 0.6)", // Cyan
  "rgba(245, 106, 0, 0.6)", // Dark orange
  "rgba(139, 87, 42, 0.6)", // Brown
];

// Darker border colors for each product
const PRODUCT_BORDER_COLORS = [
  "rgba(24, 144, 255, 0.9)",
  "rgba(82, 196, 26, 0.9)",
  "rgba(245, 34, 45, 0.9)",
  "rgba(250, 173, 20, 0.9)",
  "rgba(114, 46, 209, 0.9)",
  "rgba(19, 194, 194, 0.9)",
  "rgba(245, 106, 0, 0.9)",
  "rgba(139, 87, 42, 0.9)",
];

// Styled components
const PalletContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 66.66%; /* Aspect ratio for pallet (1200:800) */
  border: 2px solid #1890ff;
  background-color: #f0f5ff;
  margin-top: 24px;
`;

const PalletVisualization = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const BoxLayer = styled.div<{ $isActive?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${(props) => (props.$isActive ? 1 : 0)};
  pointer-events: ${(props) => (props.$isActive ? "all" : "none")};
  transition: opacity 0.3s ease;
`;

const Box = styled.div<{
  $width: number;
  $length: number;
  $posX: number;
  $posY: number;
  $color?: string;
  $borderColor?: string;
}>`
  position: absolute;
  width: ${(props) => (props.$width / PALLET_LENGTH) * 100}%;
  height: ${(props) => (props.$length / PALLET_WIDTH) * 100}%;
  left: ${(props) => (props.$posX / PALLET_LENGTH) * 100}%;
  top: ${(props) => (props.$posY / PALLET_WIDTH) * 100}%;
  background-color: ${(props) => props.$color || "rgba(24, 144, 255, 0.5)"};
  border: 1px solid
    ${(props) => props.$borderColor || "rgba(24, 144, 255, 0.8)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #000;
  overflow: hidden;
`;

const DimensionLabel = styled.div<{
  $position: "top" | "left";
  $offset?: number;
}>`
  position: absolute;
  ${(props) =>
    props.$position === "top"
      ? "top: -25px; left: 50%;"
      : "left: -25px; top: 50%;"}
  ${(props) =>
    props.$position === "top"
      ? "transform: translateX(-50%);"
      : "transform: translateY(-50%) rotate(-90deg);"}
  font-size: 12px;
  color: #1890ff;
  font-weight: bold;
`;

// 3D Pallet visualization components
const Pallet3DContainer = styled.div<{ $layerIndex?: number }>`
  position: relative;
  width: 100%;
  height: 400px;
  perspective: 1000px;
  margin-top: ${(props) => Math.min(props.$layerIndex || 0, 10) * 15}px;
  padding-top: 100px;
  padding-bottom: 40px;
  margin-bottom: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Pallet3D = styled.div<{ $rotateX: number; $rotateZ: number }>`
  position: relative;
  width: 300px; /* 120cm scaled */
  height: 200px; /* 80cm scaled - maintains 3:2 ratio (120:80) */
  margin: 0 auto;
  transform-style: preserve-3d;
  transform: rotateX(${(props) => props.$rotateX}deg)
    rotateZ(${(props) => props.$rotateZ}deg);
  transition: transform 0.1s ease;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const PalletBase = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #be8c63; /* Wooden color for pallet */
  transform: translateZ(-10px);
  border: 2px solid #906a44;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 4px;

  /* Create wooden slats effect */
  background-image: 
    /* Horizontal top deck boards */ linear-gradient(
      90deg,
      #be8c63 0%,
      #be8c63 14%,
      #a67747 14%,
      #a67747 16%,
      #be8c63 16%,
      #be8c63 48%,
      #a67747 48%,
      #a67747 50%,
      #be8c63 50%,
      #be8c63 82%,
      #a67747 82%,
      #a67747 84%,
      #be8c63 84%,
      #be8c63 100%
    ),
    /* Vertical blocks */
      linear-gradient(
        0deg,
        transparent 0%,
        transparent 40%,
        #906a44 40%,
        #906a44 60%,
        transparent 60%,
        transparent 100%
      );
  background-size: 100% 100%, 33.33% 100%;
  background-repeat: no-repeat, repeat-x;
  background-position: center;

  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    /* Bottom deck boards */
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      transparent 14%,
      #a67747 14%,
      #a67747 16%,
      transparent 16%,
      transparent 48%,
      #a67747 48%,
      #a67747 50%,
      transparent 50%,
      transparent 82%,
      #a67747 82%,
      #a67747 84%,
      transparent 84%,
      transparent 100%
    );
    transform: translateZ(-5px);
  }
`;

const BoxLayer3D = styled.div<{
  $layerIndex: number;
  $isHighlighted?: boolean;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: translateZ(${(props) => props.$layerIndex}px);
  z-index: ${(props) => Math.round(props.$layerIndex)};
  &:hover {
    cursor: pointer;
  }
`;

const Box3D = styled.div<{
  $width: number;
  $length: number;
  $posX: number;
  $posY: number;
  $height: number;
  $color?: string;
  $borderColor?: string;
  $isHighlighted?: boolean;
}>`
  position: absolute;
  width: ${(props) => (props.$width / PALLET_LENGTH) * 100}%;
  height: ${(props) => (props.$length / PALLET_WIDTH) * 100}%;
  left: ${(props) => (props.$posX / PALLET_LENGTH) * 100}%;
  top: ${(props) => (props.$posY / PALLET_WIDTH) * 100}%;
  background-color: ${(props) =>
    props.$isHighlighted
      ? "rgba(255, 255, 255, 0.9)"
      : props.$color || "rgba(24, 144, 255, 1)"};
  border: 1px solid black;
  transform-style: preserve-3d !important;
  transform: translateZ(0);

  /* Top face */
  &::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${(props) =>
      props.$isHighlighted
        ? "rgba(255, 255, 255, 0.9)"
        : props.$color || "rgba(24, 144, 255, 1)"};
    border: 1px solid black;
    transform: translateZ(${(props) => props.$height}px);
    top: 0;
    left: 0;
  }

  /* Front face */
  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: ${(props) => props.$height}px;
    background-color: ${(props) =>
      props.$isHighlighted
        ? "rgba(255, 255, 255, 0.9)"
        : props.$color || "rgba(24, 144, 255, 1)"};
    border: 1px solid black;
    transform: rotateX(90deg);
    transform-origin: bottom;
    bottom: 0;
    left: 0;
  }

  /* Right face */
  &:before {
    content: "";
    position: absolute;
    width: ${(props) => props.$height}px;
    height: 100%;
    background-color: ${(props) =>
      props.$isHighlighted
        ? "rgba(255, 255, 255, 0.9)"
        : props.$color || "rgba(24, 144, 255, 1)"};
    border: 1px solid black;
    transform: rotateY(90deg);
    transform-origin: right;
    top: 0;
    right: 0;
  }
`;

// New styled components for the side view visualization
const SideViewContainer = styled.div`
  position: relative;
  width: 100px;
  height: 80px;
  margin-top: 8px;
  background-color: #f0f5ff;
  border: 1px solid #d9d9d9;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const SideViewPalletBase = styled.div`
  width: 100%;
  height: 8px;
  background-color: #d9d9d9;
  border: 1px solid #1890ff;
`;

const LayerIndicator = styled.div<{
  $active: boolean;
  $index: number;
  $total: number;
}>`
  width: 100%;
  height: ${(props) => (72 - 8) / props.$total}px;
  background-color: ${(props) =>
    props.$active ? "rgba(24, 144, 255, 0.8)" : "rgba(24, 144, 255, 0.3)"};
  border: 1px solid
    ${(props) =>
      props.$active ? "rgba(24, 144, 255, 1)" : "rgba(24, 144, 255, 0.6)"};
  transition: all 0.3s ease;
`;

interface CustomProduct {
  id: string;
  name: string;
  carton_length: number;
  carton_width: number;
  carton_height: number;
  pieces_per_carton?: number;
  isCustom: true;
}

type Product = CompanyProduct | CustomProduct;

interface SelectedProduct {
  product: Product;
  quantity: number;
  maxQuantity: number; // Maximum quantity that can fit on pallet
  orientation: "length-width" | "width-length";
  locked: boolean; // Flag to indicate if the quantity is locked
}

interface PalletCalculationResult {
  totalBoxes: number;
  boxesPerLayer: number;
  layers: number;
  palletUtilization: number;
  totalProducts: number;
  palletHeight: number;
  remainingHeight: number; // Added to track remaining height available
  cartonUtilization: number; // Added for per-carton volume utilization
}

// Add a scaling factor for height in the 3D visualization for better proportions
const HEIGHT_SCALE_FACTOR = 1.5; // Adjust this to get the right visual proportions

export const LVBPalletCalculator: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const { products, loading } = useCompanyProducts(selectedCompany);
  const [customProductForm] = Form.useForm();

  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );
  const [calculationResult, setCalculationResult] =
    useState<PalletCalculationResult | null>(null);
  const [customProductModalVisible, setCustomProductModalVisible] =
    useState(false);
  const [customProducts, setCustomProducts] = useState<CustomProduct[]>([]);
  const [visualizationType, setVisualizationType] = useState<"2d" | "3d">("2d");
  const [currentLayer, setCurrentLayer] = useState(1);

  // Add state for 3D rotation
  const [rotation, setRotation] = useState({ x: 45, z: -45 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    trackFeatureUsage("tools_lvb_pallet_calculator_view");
  }, []);

  // Add useEffect to handle window-level mouse events for smoother dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Calculate how much the mouse has moved
      const deltaX = e.clientX - lastMousePosition.x;
      const deltaY = e.clientY - lastMousePosition.y;

      // Update rotation based on mouse movement
      setRotation({
        x: Math.min(Math.max(rotation.x - deltaY * 0.5, 0), 90), // Limit rotation on X axis
        z: rotation.z - deltaX * 0.5, // No limits on Z rotation
      });

      setLastMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    // Only add listeners when dragging starts
    if (isDragging) {
      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleGlobalMouseUp);
    }

    // Clean up event listeners
    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, lastMousePosition, rotation]);

  const handleAddProduct = (productId: string | number) => {
    // Find the selected product (either from company products or custom products)
    let product: Product | undefined;

    if (typeof productId === "string" && productId.startsWith("custom-")) {
      product = customProducts.find((p) => p.id === productId);
    } else {
      product = products.find((p) => p.id === Number(productId));
    }

    if (product) {
      // Validate product dimensions to prevent calculation issues
      const minDimension = 0.1; // Minimum dimension in cm
      const maxBoxesPerLayer = 5000; // Maximum boxes per layer to prevent browser freeze

      if (
        !product.carton_length ||
        !product.carton_width ||
        !product.carton_height ||
        product.carton_length < minDimension ||
        product.carton_width < minDimension ||
        product.carton_height < minDimension
      ) {
        Modal.error({
          title: t(
            "tools.lvbPalletCalculator.validation.invalidDimensionsTitle"
          ),
          content: t(
            "tools.lvbPalletCalculator.validation.invalidDimensionsContent",
            {
              productName: product.name,
              minDimension: minDimension,
            }
          ),
        });
        return;
      }
      // Determine the best orientation by calculating which one fits more boxes
      const lengthWidthOrientation = "length-width";
      const widthLengthOrientation = "width-length";

      // Calculate for length-width orientation
      const [lwBoxWidth, lwBoxLength] = getBoxDimensions(
        product,
        lengthWidthOrientation
      );
      const lwBoxesInLength = Math.floor(PALLET_LENGTH / lwBoxLength);
      const lwBoxesInWidth = Math.floor(PALLET_WIDTH / lwBoxWidth);
      const lwBoxesPerLayer = lwBoxesInLength * lwBoxesInWidth;

      // Calculate for width-length orientation
      const [wlBoxWidth, wlBoxLength] = getBoxDimensions(
        product,
        widthLengthOrientation
      );
      const wlBoxesInLength = Math.floor(PALLET_LENGTH / wlBoxLength);
      const wlBoxesInWidth = Math.floor(PALLET_WIDTH / wlBoxWidth);
      const wlBoxesPerLayer = wlBoxesInLength * wlBoxesInWidth;

      // Check if either orientation would create too many boxes
      if (
        lwBoxesPerLayer > maxBoxesPerLayer ||
        wlBoxesPerLayer > maxBoxesPerLayer
      ) {
        Modal.error({
          title: t(
            "tools.lvbPalletCalculator.validation.dimensionsTooSmallTitle"
          ),
          content: t(
            "tools.lvbPalletCalculator.validation.dimensionsTooSmallContent",
            {
              productName: product.name,
              boxCount: Math.max(
                lwBoxesPerLayer,
                wlBoxesPerLayer
              ).toLocaleString(),
              maxBoxes: maxBoxesPerLayer.toLocaleString(),
            }
          ),
        });
        return;
      }

      // Determine the best orientation based on which fits more boxes per layer
      const bestOrientation =
        lwBoxesPerLayer >= wlBoxesPerLayer
          ? lengthWidthOrientation
          : widthLengthOrientation;

      // Calculate using the best orientation
      const [boxWidth, boxLength] = getBoxDimensions(product, bestOrientation);
      const boxesInLength = Math.floor(PALLET_LENGTH / boxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / boxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;
      const boxHeight = product.carton_height || 0;

      // For single product, just use the available height
      const remainingHeight = AVAILABLE_HEIGHT;
      const layers = Math.floor(remainingHeight / boxHeight);
      const maxQuantity = boxesPerLayer * layers;

      // Set initial quantity to maximum
      const quantity = maxQuantity;

      // Replace any existing product with the new one (single product mode)
      const newSelectedProduct = {
        product,
        quantity,
        maxQuantity,
        orientation: bestOrientation as "length-width" | "width-length",
        locked: false,
      };

      setSelectedProducts([newSelectedProduct]);

      // Recalculate the pallet with just this product
      calculatePalletStacking([newSelectedProduct]);
    }
  };

  const handleRemoveProduct = (index: number) => {
    const updatedProducts = selectedProducts.filter((_, i) => i !== index);
    setSelectedProducts(updatedProducts);

    // Recalculate with the updated products list
    if (updatedProducts.length > 0) {
      calculatePalletStacking(updatedProducts);
    } else {
      setCalculationResult(null);
    }
  };

  const handleAddCustomProduct = () => {
    customProductForm.validateFields().then((values) => {
      // Validate dimensions before creating the product
      const minDimension = 0.1; // Minimum dimension in cm
      const maxBoxesPerLayer = 5000; // Maximum boxes per layer to prevent browser freeze

      if (
        values.length < minDimension ||
        values.width < minDimension ||
        values.height < minDimension
      ) {
        Modal.error({
          title: t(
            "tools.lvbPalletCalculator.validation.invalidDimensionsTitle"
          ),
          content: t(
            "tools.lvbPalletCalculator.validation.customInvalidDimensionsContent",
            {
              minDimension: minDimension,
            }
          ),
        });
        return;
      }

      const newCustomProduct: CustomProduct = {
        id: `custom-${Date.now()}`,
        name: values.name,
        // Store values as is, since they're already in cm
        carton_length: values.length,
        carton_width: values.width,
        carton_height: values.height,
        pieces_per_carton: values.pieces_per_carton || 1,
        isCustom: true,
      };

      // Instead of adding to customProducts list, add directly to selectedProducts
      const lengthWidthOrientation = "length-width" as const;
      const widthLengthOrientation = "width-length" as const;

      // Calculate for length-width orientation
      const [lwBoxWidth, lwBoxLength] = getBoxDimensions(
        newCustomProduct,
        lengthWidthOrientation
      );
      const lwBoxesInLength = Math.floor(PALLET_LENGTH / lwBoxLength);
      const lwBoxesInWidth = Math.floor(PALLET_WIDTH / lwBoxWidth);
      const lwBoxesPerLayer = lwBoxesInLength * lwBoxesInWidth;

      // Calculate for width-length orientation
      const [wlBoxWidth, wlBoxLength] = getBoxDimensions(
        newCustomProduct,
        widthLengthOrientation
      );
      const wlBoxesInLength = Math.floor(PALLET_LENGTH / wlBoxLength);
      const wlBoxesInWidth = Math.floor(PALLET_WIDTH / wlBoxWidth);
      const wlBoxesPerLayer = wlBoxesInLength * wlBoxesInWidth;

      // Check if either orientation would create too many boxes
      if (
        lwBoxesPerLayer > maxBoxesPerLayer ||
        wlBoxesPerLayer > maxBoxesPerLayer
      ) {
        Modal.error({
          title: t(
            "tools.lvbPalletCalculator.validation.dimensionsTooSmallTitle"
          ),
          content: t(
            "tools.lvbPalletCalculator.validation.customDimensionsTooSmallContent",
            {
              boxCount: Math.max(
                lwBoxesPerLayer,
                wlBoxesPerLayer
              ).toLocaleString(),
              maxBoxes: maxBoxesPerLayer.toLocaleString(),
            }
          ),
        });
        return;
      }

      // Determine the best orientation based on which fits more boxes per layer
      const bestOrientation =
        lwBoxesPerLayer >= wlBoxesPerLayer
          ? lengthWidthOrientation
          : widthLengthOrientation;

      // Calculate using the best orientation
      const [boxWidth, boxLength] = getBoxDimensions(
        newCustomProduct,
        bestOrientation
      );
      const boxesInLength = Math.floor(PALLET_LENGTH / boxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / boxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;

      // For single product mode, just use the available height
      const remainingHeight = AVAILABLE_HEIGHT;
      const boxHeight = newCustomProduct.carton_height || 0;
      const layers = Math.floor(remainingHeight / boxHeight);
      const maxQuantity = boxesPerLayer * layers;

      // Set initial quantity to maximum
      const quantity = maxQuantity;

      // Replace any existing product with this custom product (single product mode)
      const newSelectedProduct = {
        product: newCustomProduct,
        quantity,
        maxQuantity,
        orientation: bestOrientation as "length-width" | "width-length",
        locked: false,
      };

      setSelectedProducts([newSelectedProduct]);
      setCustomProductModalVisible(false);
      customProductForm.resetFields();

      // We still add to customProducts list for dropdown display purposes
      setCustomProducts([...customProducts, newCustomProduct]);

      // Recalculate with just this product
      calculatePalletStacking([newSelectedProduct]);
    });
  };

  const handleQuantityChange = (index: number, newQuantity: number) => {
    const updatedProducts = [...selectedProducts];
    updatedProducts[index].quantity = newQuantity;

    // Recalculate the pallet with the new quantities
    calculatePalletStacking(updatedProducts, index);
  };

  // Modify the calculation function to be simpler for a single product
  const calculatePalletStacking = (
    products = selectedProducts,
    changedIndex: number | null = null
  ) => {
    if (products.length === 0) return;

    // For single product mode, just use the first product
    const product = products[0];

    let totalBoxes = 0;
    let totalProducts = 0;
    let palletHeight = PALLET_HEIGHT;
    let remainingHeight = AVAILABLE_HEIGHT;
    let boxesPerLayer = 0;
    let layers = 0;
    let palletUtilization = 0;
    let cartonUtilization = 0;

    // Calculate total volume of pallet
    const palletVolume = PALLET_WIDTH * PALLET_LENGTH * AVAILABLE_HEIGHT;
    let totalVolume = 0;
    let totalCartonVolume = 0;

    const boxHeight = product.product.carton_height || 0;

    // Calculate how many boxes fit in a single layer
    const [boxWidth, boxLength] = getBoxDimensions(
      product.product,
      product.orientation
    );

    const boxesInLength = Math.floor(PALLET_LENGTH / boxLength);
    const boxesInWidth = Math.floor(PALLET_WIDTH / boxWidth);
    const productBoxesPerLayer = boxesInLength * boxesInWidth;

    // Calculate actual layers used by this product
    const productLayers = Math.ceil(product.quantity / productBoxesPerLayer);

    // Calculate actual boxes used
    const actualBoxes = Math.min(
      product.quantity,
      productLayers * productBoxesPerLayer
    );

    // Calculate product stats
    totalBoxes = actualBoxes;

    const piecesPerCarton =
      "pieces_per_carton" in product.product
        ? product.product.pieces_per_carton || 1
        : 1;

    totalProducts = actualBoxes * piecesPerCarton;

    // Update height used
    const heightUsed = productLayers * boxHeight;
    palletHeight = PALLET_HEIGHT + heightUsed;
    remainingHeight = AVAILABLE_HEIGHT - heightUsed;

    // Set reference values for display
    boxesPerLayer = productBoxesPerLayer;
    layers = productLayers;

    // Calculate volume utilization
    const boxVolume = boxWidth * boxLength * boxHeight;
    const layerVolume = boxLength * boxWidth * boxHeight * productBoxesPerLayer;

    // For partial layers, we need to adjust the volume calculation
    const lastLayerBoxCount =
      actualBoxes % productBoxesPerLayer || productBoxesPerLayer;
    const fullLayers = Math.floor(actualBoxes / productBoxesPerLayer);

    // Volume of full layers + volume of partial last layer
    const allocatedVolume =
      fullLayers * layerVolume + lastLayerBoxCount * boxVolume;

    totalVolume = allocatedVolume;

    // Calculate the actual volume of the cartons (exact number of boxes)
    const actualCartonVolume = actualBoxes * boxVolume;
    totalCartonVolume = actualCartonVolume;

    // Calculate the overall pallet utilization
    palletUtilization = totalVolume / palletVolume;

    // Calculate the carton utilization - boxes volume divided by total pallet volume
    cartonUtilization = totalCartonVolume / palletVolume;

    // Update the product list with new max quantities if needed
    if (changedIndex !== null) {
      setSelectedProducts(products);
    }

    // Update calculation result
    setCalculationResult({
      totalBoxes,
      boxesPerLayer,
      layers,
      palletUtilization,
      cartonUtilization,
      totalProducts,
      palletHeight,
      remainingHeight,
    });

    trackFeatureUsage("tools_lvb_pallet_calculator_calculate");
  };

  // Get box dimensions in cm for internal calculations
  const getBoxDimensions = (
    product: Product,
    orientation: string
  ): [number, number] => {
    if (orientation === "length-width") {
      return [product.carton_width || 0, product.carton_length || 0];
    } else {
      return [product.carton_length || 0, product.carton_width || 0];
    }
  };

  // Generate boxes for visualization
  const generateBoxes = (layerIndex: number) => {
    if (!calculationResult || selectedProducts.length === 0) return null;

    // Safety check to prevent rendering too many boxes
    const maxBoxesPerVisualization = 1000;
    if (calculationResult.boxesPerLayer > maxBoxesPerVisualization) {
      return (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "16px",
            borderRadius: "8px",
            border: "2px solid #ff4d4f",
            textAlign: "center",
            maxWidth: "300px",
          }}
        >
          <div
            style={{
              color: "#ff4d4f",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            {t(
              "tools.lvbPalletCalculator.validation.visualizationDisabledTitle"
            )}
          </div>
          <div style={{ fontSize: "12px" }}>
            {t(
              "tools.lvbPalletCalculator.validation.visualizationDisabledContent",
              {
                boxCount: calculationResult.boxesPerLayer.toLocaleString(),
              }
            )}
          </div>
        </div>
      );
    }

    // Sort products for pyramid stacking (similar to calculation function)
    const lockedProducts = selectedProducts.filter((p) => p.locked);
    const unlockedProducts = selectedProducts.filter((p) => !p.locked);

    // Sort unlocked products by surface area (largest first)
    const sortedUnlockedProducts = [...unlockedProducts].sort((a, b) => {
      const [aWidth, aLength] = getBoxDimensions(a.product, a.orientation);
      const [bWidth, bLength] = getBoxDimensions(b.product, b.orientation);

      // Calculate surface areas
      const aArea = aWidth * aLength;
      const bArea = bWidth * bLength;

      return bArea - aArea;
    });

    // Combine for display order
    const sortedProducts = [...lockedProducts, ...sortedUnlockedProducts];

    // Track box positions already used
    const usedPositions = new Set<string>();
    const boxes = [];

    // Track the current layer index across all products
    let currentTotalLayer = 0;

    // Track previous layer dimensions for pyramid stacking
    let prevLayerMaxWidth = PALLET_WIDTH;
    let prevLayerMaxLength = PALLET_LENGTH;

    // Process products one by one
    for (
      let productIndex = 0;
      productIndex < sortedProducts.length;
      productIndex++
    ) {
      const currentProduct = sortedProducts[productIndex];
      const [boxWidth, boxLength] = getBoxDimensions(
        currentProduct.product,
        currentProduct.orientation
      );

      // Apply pyramid stacking constraints for unlocked products
      let adjustedBoxWidth = boxWidth;
      let adjustedBoxLength = boxLength;

      if (!currentProduct.locked) {
        adjustedBoxWidth = Math.min(boxWidth, prevLayerMaxWidth);
        adjustedBoxLength = Math.min(boxLength, prevLayerMaxLength);

        // Update for the next layer
        prevLayerMaxWidth = adjustedBoxWidth;
        prevLayerMaxLength = adjustedBoxLength;
      }

      const boxesInLength = Math.floor(PALLET_LENGTH / adjustedBoxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / adjustedBoxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;

      // Calculate how many full pallets this product needs
      const totalBoxes = currentProduct.quantity;
      const boxesPerPallet =
        boxesPerLayer *
        Math.floor(
          AVAILABLE_HEIGHT / (currentProduct.product.carton_height || 1)
        );
      const fullPallets = Math.floor(totalBoxes / boxesPerPallet);
      const remainingBoxes = totalBoxes % boxesPerPallet;

      // Calculate layers for remaining boxes (partial pallet)
      const remainingLayers = Math.ceil(remainingBoxes / boxesPerLayer);

      // Total layers for this product across all pallets
      const totalProductLayers =
        fullPallets *
          Math.floor(
            AVAILABLE_HEIGHT / (currentProduct.product.carton_height || 1)
          ) +
        (remainingBoxes > 0 ? remainingLayers : 0);

      // Check if this product appears in the current layer
      const productStartLayer = currentTotalLayer + 1;
      const productEndLayer = currentTotalLayer + totalProductLayers;

      if (layerIndex >= productStartLayer && layerIndex <= productEndLayer) {
        // This product is in the current layer
        // Calculate which boxes in this layer belong to this product
        const layerRelativeIndex = layerIndex - productStartLayer;

        // Calculate which pallet and which layer within that pallet
        const layersPerPallet = Math.floor(
          AVAILABLE_HEIGHT / (currentProduct.product.carton_height || 1)
        );
        const palletIndex = Math.floor(layerRelativeIndex / layersPerPallet);
        const layerInPallet = layerRelativeIndex % layersPerPallet;

        // Calculate boxes in this specific layer
        let boxesInThisLayer;

        if (palletIndex < fullPallets) {
          // This is a full pallet
          boxesInThisLayer = boxesPerLayer;
        } else {
          // This is the last (potentially partial) pallet
          const remainingBoxesInLayer = Math.min(
            boxesPerLayer,
            remainingBoxes - layerInPallet * boxesPerLayer
          );
          boxesInThisLayer = Math.max(0, remainingBoxesInLayer);
        }

        // Select color for this product (cycle through colors)
        const colorIndex = productIndex % PRODUCT_COLORS.length;
        const boxColor = PRODUCT_COLORS[colorIndex];
        const borderColor = PRODUCT_BORDER_COLORS[colorIndex];

        // Add boxes for this product to the visualization
        for (let i = 0; i < boxesInThisLayer; i++) {
          const row = Math.floor(i / boxesInLength);
          const col = i % boxesInLength;

          const posX = col * adjustedBoxLength;
          const posY = row * adjustedBoxWidth;

          // Check if position is already used
          const posKey = `${posX}-${posY}`;
          if (!usedPositions.has(posKey)) {
            usedPositions.add(posKey);

            boxes.push(
              <Box
                key={`box-${layerIndex}-${productIndex}-${row}-${col}`}
                $width={adjustedBoxLength}
                $length={adjustedBoxWidth}
                $posX={posX}
                $posY={posY}
                $color={boxColor}
                $borderColor={borderColor}
              />
            );
          }
        }
      }

      // Update the total layer count
      currentTotalLayer += totalProductLayers;
    }

    return boxes;
  };

  // Generate all layers for 2D visualization
  const generateLayers = () => {
    if (!calculationResult || selectedProducts.length === 0) return null;

    const layers = [];

    // Sort products for pyramid stacking (similar to calculation function)
    const lockedProducts = selectedProducts.filter((p) => p.locked);
    const unlockedProducts = selectedProducts.filter((p) => !p.locked);

    // Sort unlocked products by surface area (largest first)
    const sortedUnlockedProducts = [...unlockedProducts].sort((a, b) => {
      const [aWidth, aLength] = getBoxDimensions(a.product, a.orientation);
      const [bWidth, bLength] = getBoxDimensions(b.product, b.orientation);

      // Calculate surface areas
      const aArea = aWidth * aLength;
      const bArea = bWidth * bLength;

      return bArea - aArea;
    });

    // Combine for display order - reversed to place largest at bottom
    const sortedProducts = [
      ...sortedUnlockedProducts.reverse(),
      ...lockedProducts,
    ];

    // Calculate the total number of layers needed for all products combined
    let totalLayers = 0;
    for (let i = 0; i < sortedProducts.length; i++) {
      const product = sortedProducts[i];
      const [boxWidth, boxLength] = getBoxDimensions(
        product.product,
        product.orientation
      );
      const boxesInLength = Math.floor(PALLET_LENGTH / boxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / boxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;

      // Calculate how many full pallets this product needs
      const totalBoxes = product.quantity;
      const boxesPerPallet =
        boxesPerLayer *
        Math.floor(AVAILABLE_HEIGHT / (product.product.carton_height || 1));
      const fullPallets = Math.floor(totalBoxes / boxesPerPallet);
      const remainingBoxes = totalBoxes % boxesPerPallet;

      // Calculate layers for remaining boxes (partial pallet)
      const remainingLayers = Math.ceil(remainingBoxes / boxesPerLayer);

      // Total layers for this product across all pallets
      const productLayers =
        fullPallets *
          Math.floor(AVAILABLE_HEIGHT / (product.product.carton_height || 1)) +
        (remainingBoxes > 0 ? remainingLayers : 0);

      totalLayers += productLayers;
    }

    // Generate layers up to the total needed
    for (let layer = 1; layer <= totalLayers; layer++) {
      layers.push(
        <BoxLayer key={`layer-2d-${layer}`} $isActive={layer === currentLayer}>
          {generateBoxes(layer)}
        </BoxLayer>
      );
    }

    return layers;
  };

  // Add mouse event handlers for 3D rotation
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setLastMousePosition({ x: e.clientX, y: e.clientY });
    e.preventDefault(); // Prevent text selection during drag
  };

  const resetRotation = () => {
    setRotation({ x: 45, z: -45 }); // Reset to default rotation
  };

  // Modified 3D box visualization function
  const generate3DBoxes = () => {
    if (!calculationResult || selectedProducts.length === 0) return null;

    // Safety check to prevent rendering too many boxes in 3D
    const maxBoxesPerVisualization = 1000;
    if (calculationResult.boxesPerLayer > maxBoxesPerVisualization) {
      return null; // Don't render 3D visualization for products with too many boxes
    }

    // Track layer heights for positioning - start at pallet base height
    let currentLayerHeight = 0;
    const layers = [];
    let layerIndex = 0;

    // Sort products by surface area for pyramid stacking (largest first)
    const lockedProducts = selectedProducts.filter((p) => p.locked);
    const unlockedProducts = selectedProducts.filter((p) => !p.locked);

    // Sort unlocked products by surface area (largest first)
    const sortedUnlockedProducts = [...unlockedProducts].sort((a, b) => {
      const [aWidth, aLength] = getBoxDimensions(a.product, a.orientation);
      const [bWidth, bLength] = getBoxDimensions(b.product, b.orientation);

      // Calculate surface areas
      const aArea = aWidth * aLength;
      const bArea = bWidth * bLength;

      return bArea - aArea;
    });

    // Combine for final display order - Place largest products at the BOTTOM by reversing
    const sortedProducts = [
      ...sortedUnlockedProducts.reverse(),
      ...lockedProducts,
    ];

    // Calculate the total number of layers for all products - used to limit visualization
    const totalLayers = sortedProducts.reduce((acc, product) => {
      const [boxWidth, boxLength] = getBoxDimensions(
        product.product,
        product.orientation
      );
      const boxesInLength = Math.floor(PALLET_LENGTH / boxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / boxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;

      // Calculate how many full pallets this product needs
      const totalBoxes = product.quantity;
      const boxesPerPallet =
        boxesPerLayer *
        Math.floor(AVAILABLE_HEIGHT / (product.product.carton_height || 1));
      const fullPallets = Math.floor(totalBoxes / boxesPerPallet);
      const remainingBoxes = totalBoxes % boxesPerPallet;

      // Calculate layers for remaining boxes (partial pallet)
      const remainingLayers = Math.ceil(remainingBoxes / boxesPerLayer);

      // Total layers for this product across all pallets
      const productLayers =
        fullPallets *
          Math.floor(AVAILABLE_HEIGHT / (product.product.carton_height || 1)) +
        (remainingBoxes > 0 ? remainingLayers : 0);

      return acc + productLayers;
    }, 0);

    // Limit visualization to 15 layers for performance
    const maxLayersToShow = Math.min(totalLayers, 15);

    // Track the previous layer's dimensions for pyramid stacking
    let prevLayerMaxWidth = PALLET_WIDTH;
    let prevLayerMaxLength = PALLET_LENGTH;

    // Generate boxes for each product
    for (
      let productIndex = 0;
      productIndex < sortedProducts.length;
      productIndex++
    ) {
      const product = sortedProducts[productIndex];
      const [boxWidth, boxLength] = getBoxDimensions(
        product.product,
        product.orientation
      );

      // Apply pyramid stacking constraints for unlocked products
      let adjustedBoxWidth = boxWidth;
      let adjustedBoxLength = boxLength;

      if (!product.locked) {
        adjustedBoxWidth = Math.min(boxWidth, prevLayerMaxWidth);
        adjustedBoxLength = Math.min(boxLength, prevLayerMaxLength);

        // Update for the next layer
        prevLayerMaxWidth = adjustedBoxWidth;
        prevLayerMaxLength = adjustedBoxLength;
      }

      // Use the actual product height
      const boxHeight = product.product.carton_height || 0;
      // Apply scaling factor for better visual proportions
      const scaledBoxHeight = boxHeight * HEIGHT_SCALE_FACTOR;

      const boxesInLength = Math.floor(PALLET_LENGTH / adjustedBoxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / adjustedBoxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;

      // Use product index to select color from predefined arrays
      const colorIndex = productIndex % PRODUCT_COLORS.length;
      const boxColor = PRODUCT_COLORS[colorIndex];
      const borderColor = PRODUCT_BORDER_COLORS[colorIndex];

      // Calculate how many layers this product will use
      const productLayers = Math.min(
        Math.ceil(product.quantity / boxesPerLayer),
        maxLayersToShow - layerIndex
      );

      if (productLayers <= 0) continue; // Skip if this product doesn't need any layers

      // Generate each layer for this product
      for (let l = 0; l < productLayers && layerIndex < maxLayersToShow; l++) {
        const boxes = [];

        // How many boxes to place in this layer (may be less than boxesPerLayer for the last layer)
        const remainingBoxes = product.quantity - l * boxesPerLayer;
        const boxesInThisLayer = Math.min(remainingBoxes, boxesPerLayer);

        // Create boxes for this layer
        for (let i = 0; i < boxesInThisLayer; i++) {
          // Calculate row and column using boxesInLength
          const col = i % boxesInLength;
          const row = Math.floor(i / boxesInLength);

          // Calculate position according to PALLET orientation
          const posX = col * adjustedBoxLength;
          const posY = row * adjustedBoxWidth;

          boxes.push(
            <Box3D
              key={`box-${layerIndex}-${i}`}
              $width={adjustedBoxLength}
              $length={adjustedBoxWidth}
              $posX={posX}
              $posY={posY}
              $height={scaledBoxHeight}
              $color={boxColor}
              $borderColor={borderColor}
            />
          );
        }

        // Add this layer to our layers array at the exact height of previous layers
        layers.push(
          <BoxLayer3D
            key={`layer-${layerIndex}`}
            $layerIndex={currentLayerHeight}
            $isHighlighted={false}
            onClick={() => setCurrentLayer(layerIndex + 1)}
          >
            {boxes}
          </BoxLayer3D>
        );

        // Update height by adding the exact height of this product box
        currentLayerHeight += scaledBoxHeight;
        layerIndex++;
      }
    }

    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <Pallet3D
          $rotateX={rotation.x}
          $rotateZ={rotation.z}
          onMouseDown={handleMouseDown}
        >
          <PalletBase />
          {layers}
        </Pallet3D>
      </div>
    );
  };

  // Generate layer indicators for side view
  const generateLayerIndicators = () => {
    if (!calculationResult) return null;

    // Calculate total layers for all products
    let totalLayers = 0;
    for (let i = 0; i < selectedProducts.length; i++) {
      const product = selectedProducts[i];
      const [boxWidth, boxLength] = getBoxDimensions(
        product.product,
        product.orientation
      );
      const boxesInLength = Math.floor(PALLET_LENGTH / boxLength);
      const boxesInWidth = Math.floor(PALLET_WIDTH / boxWidth);
      const boxesPerLayer = boxesInLength * boxesInWidth;

      // Calculate how many full pallets this product needs
      const totalBoxes = product.quantity;
      const boxesPerPallet =
        boxesPerLayer *
        Math.floor(AVAILABLE_HEIGHT / (product.product.carton_height || 1));
      const fullPallets = Math.floor(totalBoxes / boxesPerPallet);
      const remainingBoxes = totalBoxes % boxesPerPallet;

      // Calculate layers for remaining boxes (partial pallet)
      const remainingLayers = Math.ceil(remainingBoxes / boxesPerLayer);

      // Total layers for this product across all pallets
      const productLayers =
        fullPallets *
          Math.floor(AVAILABLE_HEIGHT / (product.product.carton_height || 1)) +
        (remainingBoxes > 0 ? remainingLayers : 0);

      totalLayers += productLayers;
    }

    const layers = [];
    for (let i = totalLayers; i >= 1; i--) {
      layers.push(
        <LayerIndicator
          key={`layer-indicator-${i}`}
          $active={i === currentLayer}
          $index={i}
          $total={totalLayers}
        />
      );
    }

    return layers;
  };

  // Render legend for products
  const renderLegend = () => {
    if (!selectedProducts.length) return null;

    return (
      <LegendContainer>
        {selectedProducts.map((product, index) => {
          const colorIndex = index % PRODUCT_COLORS.length;
          const boxColor = PRODUCT_COLORS[colorIndex];
          const borderColor = PRODUCT_BORDER_COLORS[colorIndex];

          return (
            <LegendItem key={`legend-${index}`}>
              <LegendColor $color={boxColor} $borderColor={borderColor} />
              <Text>{product.product.name}</Text>
            </LegendItem>
          );
        })}
      </LegendContainer>
    );
  };

  const productOptions = [
    ...products.map((product) => {
      const hasDimensions =
        product.carton_width && product.carton_length && product.carton_height;
      return (
        <Option
          key={product.id}
          value={product.id}
          disabled={!hasDimensions}
          title={
            hasDimensions
              ? undefined
              : "This product is missing dimensions. Update in My Products."
          }
        >
          {product.name}
          {hasDimensions
            ? ` (${product.carton_length}x${product.carton_width}x${product.carton_height} cm)`
            : " (⚠️ Missing dimensions)"}
        </Option>
      );
    }),
    ...customProducts.map((product) => (
      <Option key={product.id} value={product.id}>
        {product.name} ({product.carton_length}x{product.carton_width}x
        {product.carton_height} cm (Custom))
      </Option>
    )),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/tools")}
            style={{ marginBottom: 16, padding: 0 }}
          >
            {t("common.back")}
          </Button>
          <Title level={2}>{t("tools.lvbPalletCalculator.title")}</Title>
        </div>

        <Collapse defaultActiveKey={[]} style={{ marginBottom: 16 }}>
          <Collapse.Panel
            header={t("tools.lvbPalletCalculator.specifications")}
            key="specifications"
          >
            <ul>
              <li>
                {t(
                  "tools.lvbPalletCalculator.palletDimensions",
                  "Dimensions: 80 x 120 cm"
                )}
              </li>
              <li>
                {t(
                  "tools.lvbPalletCalculator.palletHeightCm",
                  "Pallet height: 14.4 cm"
                )}
              </li>
              <li>
                {t(
                  "tools.lvbPalletCalculator.maxStackingHeight",
                  "Maximum stacking height: 1.80 m (including pallet)"
                )}
              </li>
              <li>
                {t(
                  "tools.lvbPalletCalculator.wrappingRequirement",
                  "Pallet should be wrapped with transparent plastic foil"
                )}
              </li>
              <li>
                {t(
                  "tools.lvbPalletCalculator.extendWarning",
                  "Products should not extend beyond the pallet dimensions"
                )}
              </li>
            </ul>
          </Collapse.Panel>
        </Collapse>

        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card title={t("tools.lvbPalletCalculator.addProducts")}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label={t("tools.lvbPalletCalculator.selectProduct")}
                    required
                  >
                    <Select
                      placeholder={t("tools.lvbPalletCalculator.selectProduct")}
                      loading={loading}
                      value={
                        selectedProducts.length > 0
                          ? // If a product is already selected, show its ID
                            "isCustom" in selectedProducts[0].product
                            ? selectedProducts[0].product.id
                            : selectedProducts[0].product.id
                          : undefined
                      }
                      onChange={(value) => handleAddProduct(value)}
                      notFoundContent={
                        <div style={{ textAlign: "center", padding: "8px 0" }}>
                          <Button
                            type="link"
                            icon={<PlusOutlined />}
                            onClick={() => setCustomProductModalVisible(true)}
                          >
                            {t("tools.lvbPalletCalculator.customProduct")}
                          </Button>
                        </div>
                      }
                      dropdownRender={(menu) => (
                        <div>
                          {menu}
                          <Divider style={{ margin: "8px 0" }} />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "0 8px 4px",
                            }}
                          >
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => setCustomProductModalVisible(true)}
                            >
                              {t("tools.lvbPalletCalculator.customProduct")}
                            </Button>
                            {products.some(
                              (p) =>
                                !(
                                  p.carton_width &&
                                  p.carton_length &&
                                  p.carton_height
                                )
                            ) && (
                              <Button
                                type="link"
                                onClick={() => navigate("/my-products")}
                              >
                                {t(
                                  "tools.lvbPalletCalculator.updateProductDimensions"
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    >
                      {productOptions}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <div>
                <Text strong>{t("common.selectedProducts")}:</Text>
                {selectedProducts.length > 0 && (
                  <Alert
                    message={
                      <span>
                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                        {t(
                          "tools.lvbPalletCalculator.sliderInstructions",
                          "Use the slider to adjust quantity and see the results update instantly."
                        )}
                      </span>
                    }
                    type="info"
                    style={{ marginTop: 8, marginBottom: 8 }}
                  />
                )}
                <Table
                  dataSource={selectedProducts.map((item, index) => ({
                    ...item,
                    key: index,
                  }))}
                  pagination={false}
                  size="small"
                  style={{ marginTop: 16 }}
                  columns={[
                    {
                      title: t("common.product"),
                      dataIndex: ["product", "name"],
                      key: "name",
                    },
                    {
                      title: t("tools.lvbPalletCalculator.dimensionsMm"),
                      key: "dimensions",
                      render: (_, record) => {
                        // For custom products
                        if (
                          "isCustom" in record.product &&
                          record.product.isCustom
                        ) {
                          return `${record.product.carton_length}x${record.product.carton_width}x${record.product.carton_height}`;
                        }
                        // For regular products, values are already in cm
                        return `${record.product.carton_length}x${record.product.carton_width}x${record.product.carton_height}`;
                      },
                    },
                    {
                      title: t("tools.lvbPalletCalculator.quantity"),
                      key: "quantity",
                      render: (_, record, index) => (
                        <div style={{ width: "100%" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: 4,
                            }}
                          >
                            <span>{record.quantity}</span>
                            <span style={{ color: "#999" }}>
                              Max: {record.maxQuantity}
                            </span>
                          </div>
                          <Slider
                            min={1}
                            max={record.maxQuantity}
                            value={record.quantity}
                            onChange={(value) =>
                              handleQuantityChange(index, value)
                            }
                          />
                        </div>
                      ),
                    },
                    {
                      title: (
                        <Tooltip title="Box orientation can significantly impact pallet usage efficiency">
                          <span>
                            {t("tools.lvbPalletCalculator.orientation")}{" "}
                            <InfoCircleOutlined />
                          </span>
                        </Tooltip>
                      ),
                      dataIndex: "orientation",
                      key: "orientation",
                      render: (orientation) =>
                        orientation === "length-width"
                          ? t("tools.lvbPalletCalculator.lengthWidth")
                          : t("tools.lvbPalletCalculator.widthLength"),
                    },
                    {
                      title: t("common.actions"),
                      key: "actions",
                      render: (_, __, index) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveProduct(index)}
                        />
                      ),
                    },
                  ]}
                />
              </div>

              {selectedProducts.length === 0 && (
                <div style={{ marginTop: 16 }}>
                  <Alert
                    message={t(
                      "tools.lvbPalletCalculator.addProductPrompt",
                      "Add a product from your inventory to calculate pallet stacking"
                    )}
                    type="info"
                    showIcon
                  />
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title={t("tools.lvbPalletCalculator.palletVisualization")}
              extra={
                calculationResult && (
                  <Radio.Group
                    value={visualizationType}
                    onChange={(e) => setVisualizationType(e.target.value)}
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="2d">
                      <AppstoreOutlined /> 2D
                    </Radio.Button>
                    <Radio.Button value="3d">
                      <BlockOutlined /> 3D
                    </Radio.Button>
                  </Radio.Group>
                )
              }
            >
              {calculationResult ? (
                <>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title={t("tools.lvbPalletCalculator.totalBoxes")}
                        value={calculationResult.totalBoxes}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t("tools.lvbPalletCalculator.totalProducts")}
                        value={calculationResult.totalProducts}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t("tools.lvbPalletCalculator.boxesPerLayer")}
                        value={calculationResult.boxesPerLayer}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t("tools.lvbPalletCalculator.layers")}
                        value={calculationResult.layers}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t("tools.lvbPalletCalculator.areaUtilization")}
                        value={`${(
                          calculationResult.palletUtilization * 100
                        ).toFixed(1)}%`}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t("tools.lvbPalletCalculator.palletHeight")}
                        value={`${calculationResult.palletHeight.toFixed(
                          1
                        )} cm`}
                        suffix={
                          calculationResult.palletHeight > MAX_HEIGHT ? (
                            <Tooltip
                              title={t(
                                "tools.lvbPalletCalculator.exceedsHeight"
                              )}
                            >
                              <Badge status="error" />
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={t(
                                "tools.lvbPalletCalculator.withinHeight"
                              )}
                            >
                              <Badge status="success" />
                            </Tooltip>
                          )
                        }
                      />
                    </Col>
                  </Row>

                  {visualizationType === "2d" ? (
                    <>
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <Text strong>
                          {t("tools.lvbPalletCalculator.topView")}
                        </Text>
                      </div>
                      <Row gutter={[24, 16]}>
                        <Col xs={24} lg={21}>
                          <PalletContainer>
                            <DimensionLabel $position="top">
                              120 cm
                            </DimensionLabel>
                            <DimensionLabel $position="left">
                              80 cm
                            </DimensionLabel>
                            <PalletVisualization>
                              {generateLayers()}
                            </PalletVisualization>
                          </PalletContainer>
                          {renderLegend()}
                        </Col>
                        <Col
                          xs={24}
                          lg={3}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Text strong style={{ marginBottom: 8 }}>
                            {t("tools.lvbPalletCalculator.layerView")}:{" "}
                            {currentLayer} / {calculationResult.layers}
                          </Text>
                          <SideViewContainer>
                            {generateLayerIndicators()}
                            <SideViewPalletBase />
                          </SideViewContainer>
                          <Slider
                            min={1}
                            max={(() => {
                              // Calculate total layers for all products
                              let totalLayers = 0;
                              for (
                                let i = 0;
                                i < selectedProducts.length;
                                i++
                              ) {
                                const product = selectedProducts[i];
                                const [w, l] = getBoxDimensions(
                                  product.product,
                                  product.orientation
                                );
                                const boxesInLen = Math.floor(
                                  PALLET_LENGTH / l
                                );
                                const boxesInWid = Math.floor(PALLET_WIDTH / w);
                                const boxesPerLayer = boxesInLen * boxesInWid;
                                const productLayers = Math.ceil(
                                  product.quantity / boxesPerLayer
                                );
                                totalLayers += productLayers;
                              }
                              return totalLayers > 0 ? totalLayers : 1;
                            })()}
                            value={currentLayer}
                            onChange={(value) => setCurrentLayer(value)}
                            marks={{
                              1: "1",
                              [(() => {
                                // Calculate total layers for all products
                                let totalLayers = 0;
                                for (
                                  let i = 0;
                                  i < selectedProducts.length;
                                  i++
                                ) {
                                  const product = selectedProducts[i];
                                  const [w, l] = getBoxDimensions(
                                    product.product,
                                    product.orientation
                                  );
                                  const boxesInLen = Math.floor(
                                    PALLET_LENGTH / l
                                  );
                                  const boxesInWid = Math.floor(
                                    PALLET_WIDTH / w
                                  );
                                  const boxesPerLayer = boxesInLen * boxesInWid;
                                  const productLayers = Math.ceil(
                                    product.quantity / boxesPerLayer
                                  );
                                  totalLayers += productLayers;
                                }
                                return totalLayers > 0
                                  ? totalLayers.toString()
                                  : "1";
                              })()]: (() => {
                                // Calculate total layers for all products
                                let totalLayers = 0;
                                for (
                                  let i = 0;
                                  i < selectedProducts.length;
                                  i++
                                ) {
                                  const product = selectedProducts[i];
                                  const [w, l] = getBoxDimensions(
                                    product.product,
                                    product.orientation
                                  );
                                  const boxesInLen = Math.floor(
                                    PALLET_LENGTH / l
                                  );
                                  const boxesInWid = Math.floor(
                                    PALLET_WIDTH / w
                                  );
                                  const boxesPerLayer = boxesInLen * boxesInWid;
                                  const productLayers = Math.ceil(
                                    product.quantity / boxesPerLayer
                                  );
                                  totalLayers += productLayers;
                                }
                                return totalLayers > 0
                                  ? totalLayers.toString()
                                  : "1";
                              })(),
                            }}
                            vertical
                            style={{ height: 150, margin: "16px 0" }}
                            tooltip={{
                              formatter: (value) =>
                                `${t(
                                  "tools.lvbPalletCalculator.layerView"
                                )} ${value}`,
                            }}
                          />
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <div style={{ marginTop: 24 }}>
                        <Pallet3DContainer>
                          {generate3DBoxes()}
                        </Pallet3DContainer>
                        {visualizationType === "3d" && calculationResult && (
                          <div style={{ textAlign: "center", marginTop: 8 }}>
                            <Button
                              icon={<ReloadOutlined />}
                              onClick={resetRotation}
                              size="small"
                            >
                              {t(
                                "tools.lvbPalletCalculator.resetView",
                                "Reset View"
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      <div style={{ marginTop: 24 }}>{renderLegend()}</div>
                      <div style={{ marginTop: 16 }}>
                        <Alert
                          message="Drag to rotate the 3D view for a better perspective of your pallet configuration."
                          type="info"
                          showIcon
                          style={{ marginBottom: 8 }}
                        />
                        <Text type="secondary">
                          {`Showing ${Math.min(
                            calculationResult.layers,
                            15
                          )} of ${calculationResult.layers} layers in 3D view.`}
                        </Text>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: 24,
                    color: "#999",
                  }}
                >
                  <div style={{ fontSize: 48, marginBottom: 16 }}>
                    <CalculatorOutlined />
                  </div>
                  <Paragraph>
                    {t("tools.lvbPalletCalculator.placeholderText")}
                  </Paragraph>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Space>

      {/* Custom Product Modal */}
      <Modal
        title="Add Custom Product"
        open={customProductModalVisible}
        onOk={handleAddCustomProduct}
        onCancel={() => setCustomProductModalVisible(false)}
      >
        <Form form={customProductForm} layout="vertical">
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please enter product name" }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Length (cm)"
                name="length"
                rules={[
                  { required: true, message: "Please enter length" },
                  { type: "number", min: 1, message: "Must be at least 1" },
                ]}
              >
                <InputNumber
                  min={1}
                  step={0.1}
                  style={{ width: "100%" }}
                  placeholder="Enter length in cm"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Width (cm)"
                name="width"
                rules={[
                  { required: true, message: "Please enter width" },
                  { type: "number", min: 1, message: "Must be at least 1" },
                ]}
              >
                <InputNumber
                  min={1}
                  step={0.1}
                  style={{ width: "100%" }}
                  placeholder="Enter width in cm"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Height (cm)"
                name="height"
                rules={[
                  { required: true, message: "Please enter height" },
                  { type: "number", min: 1, message: "Must be at least 1" },
                ]}
              >
                <InputNumber
                  min={1}
                  step={0.1}
                  style={{ width: "100%" }}
                  placeholder="Enter height in cm"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="pieces_per_carton"
            label="Pieces per Carton"
            initialValue={1}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Helper component for consistent stats display
const Statistic = ({
  title,
  value,
  suffix,
}: {
  title: string;
  value: string | number;
  suffix?: React.ReactNode;
}) => (
  <div style={{ marginBottom: 8 }}>
    <Text type="secondary" style={{ fontSize: 12 }}>
      {title}
    </Text>
    <div style={{ display: "flex", alignItems: "center" }}>
      <Text strong style={{ fontSize: 18 }}>
        {value}
      </Text>
      {suffix && <span style={{ marginLeft: 4 }}>{suffix}</span>}
    </div>
  </div>
);

// Add missing styled components for the popover
// const LayerPopover = styled.div`
//   position: fixed;
//   background-color: white;
//   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
//   border-radius: 8px;
//   padding: 12px;
//   z-index: 1000;
// `;

// const PopoverPalletContainer = styled.div`
//   width: 200px;
//   height: 150px;
//   border: 1px solid #f0f0f0;
//   margin-bottom: 8px;
// `;

// const PopoverTitle = styled.div`
//   font-weight: 500;
//   margin-bottom: 8px;
// `;

// Add Legend styled components
const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 32px;
  justify-content: center;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 12px;
`;

const LegendColor = styled.div<{ $color: string; $borderColor: string }>`
  width: 16px;
  height: 16px;
  background-color: ${(props) => props.$color};
  border: 1px solid ${(props) => props.$borderColor};
  margin-right: 4px;
`;

export default LVBPalletCalculator;
